import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { TaxSensitiveAcctService } from './tax-sensitive-acct.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-tax-sensitive-acct',
    templateUrl: './tax-sensitive-acct.component.html',
    styleUrls: ['./tax-sensitive-acct.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TaxSensitiveAcctComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];

  minimise = false;
  crudLoading = false;
  showFormLoader = true;

  loadSystemTaxsensitivitydata: any[] = [];
  templocalaccount: string[] = [];
  orderByField = 'FORM';
  reverseSort: boolean | '' = false;
  sort_key: any = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, '6': 0, '7': 0, '8': 0, '9': 0 };

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      const urls = typeof baseUrls === 'string' ? safeJsonInput<any>(baseUrls, null, 'tax-sensitive-acct.parse1') : baseUrls;
      if (urls && Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.tryInit();
      }
    } catch (e) {
      console.log('Base URLs input not yet stable.');
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      const header = typeof headerData === 'string' ? safeJsonInput<any>(headerData, null, 'tax-sensitive-acct.parse2') : headerData;
      if (header && Object.keys(header).length > 0) {
        this.globalService = header.globalParams || {};
        this.USER_SETTINGS = header.userSettingsData || {};
        this.GENERAL_CONFIG = header.generalConfigData || {};
        this.tryInit();
      }
    } catch (e) {
      console.log('Header data input not yet stable.');
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      const params = typeof filterParams === 'string' ? safeJsonInput<any>(filterParams, null, 'tax-sensitive-acct.parse3') : filterParams;
      if (params && Object.keys(params).length > 0) {
        this.filterParams = params;
        this.tryInit();
      }
    } catch (e) {
      console.log('Filter params input not yet stable.');
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      this.rowData = typeof rowData === 'string' ? safeJsonInput<any>(rowData, null, 'tax-sensitive-acct.parse4') : rowData || {};
      this.tryInit();
    } catch (e) {
      console.log('Row data input not yet stable.');
    }
  }

  @Input('grid-data')
  set _grid_data(gridData: string) {
    try {
      const parsed = typeof gridData === 'string' ? safeJsonInput<any>(gridData, null, 'tax-sensitive-acct.parse5') : gridData;
      this.gridData = Array.isArray(parsed) ? parsed : [];
      this.tryInit();
    } catch (e) {
      console.log('Grid data input not yet stable.');
    }
  }

  constructor(
    private taxSensitiveAcctService: TaxSensitiveAcctService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      if (!Array.isArray(this.gridData) || this.gridData.length === 0) {
        return;
      }
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    try {
      if (!this.validateSelection()) {
        this.showFormLoader = false;
        this.cdr.detectChanges();
        return;
      }
      this.fetchTaxSensitiveData();
    } catch (e) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private validateSelection(): boolean {
    const sensitiveList: string[] = [];
    const sysAcctList: any[] = [];

    if (this.rowData && Object.keys(this.rowData).length > 0) {
      sensitiveList.push(String(this.rowData.SENSITIVE_ACCT ?? ''));
      if (this.rowData.SYS_ACCT_KEY !== undefined) {
        sysAcctList.push(this.rowData.SYS_ACCT_KEY);
      }
    } else {
      (this.gridData || []).forEach((item: any) => {
        sensitiveList.push(String(item?.data?.SENSITIVE_ACCT ?? ''));
        sysAcctList.push(item?.data?.SYS_ACCT_KEY);
      });
    }

    if (sensitiveList.includes('Y')) {
      this.gtwSnackbarService.openSnackBar('Please Select TaxSensitive System Account.', 'gtw-snackbar--red');
      return false;
    }

    const uniqueSys = Array.from(new Set(sysAcctList.filter((v) => v !== undefined && v !== null)));
    if (uniqueSys.length > 1) {
      this.gtwSnackbarService.openSnackBar('Please Select Only One System Account.', 'gtw-snackbar--red');
      return false;
    }

    return true;
  }

  private fetchTaxSensitiveData(): void {
    this.isLoading = true;
    this.showFormLoader = true;
    this.taxSensitiveAcctService
      .loadTaxSensitiveData(this.GENERAL_CONFIG.base_url, this.rowData, this.gridData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.isLoading = false;
          this.showFormLoader = false;
          if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
            this.loadSystemTaxsensitivitydata = data.jsonObject.map((row: any) => ({
              ...row,
              isEditable: false,
              saveFlag: false,
              oldtemplatevalue: '',
              olduserdescvalue: '',
            }));
            this.templocalaccount = Array.from(
              new Set(this.loadSystemTaxsensitivitydata.map((r: any) => r.SCHDM_TEMPLATE_TABLE).filter(Boolean))
            );
          } else {
            this.loadSystemTaxsensitivitydata = [];
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this.showFormLoader = false;
          this.loadSystemTaxsensitivitydata = [];
          this.gtwSnackbarService.openSnackBar(
            'An error has occurred, If you continue to experience this issue please visit the help page to contact support.',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }

  sortBy(key: string, value: number): void {
    switch (value) {
      case 0:
        this.sort_key[key] = 1;
        this.reverseSort = false;
        break;
      case 1:
        this.sort_key[key] = 2;
        this.reverseSort = true;
        break;
      case 2:
        this.sort_key[key] = 0;
        this.reverseSort = '';
        break;
      default:
        this.sort_key[key] = 0;
        this.reverseSort = false;
        break;
    }
  }

  get sortedRows(): any[] {
    const rows = [...this.loadSystemTaxsensitivitydata];
    if (!this.orderByField) {
      return rows;
    }
    rows.sort((a: any, b: any) => {
      const av = String(a?.[this.orderByField] ?? '');
      const bv = String(b?.[this.orderByField] ?? '');
      return av.localeCompare(bv);
    });
    if (this.reverseSort === true) {
      rows.reverse();
    }
    return rows;
  }

  formEdit(row: any): void {
    const idx = this.loadSystemTaxsensitivitydata.findIndex((r: any) => r === row);
    if (idx < 0) {
      return;
    }
    row.isEditable = true;
    row.saveFlag = true;
    row.oldtemplatevalue = row.SCHDM_TEMPLATE_TABLE;
    row.olduserdescvalue = row.USER_DESC;
  }

  changeTemplate(row: any): void {
    row.updated = true;
  }

  formDelete(row: any): void {
    this.loadSystemTaxsensitivitydata = this.loadSystemTaxsensitivitydata.filter((r: any) => r !== row);
    const settings = this.buildDeleteSettings(row);
    const details = [settings];
    this.taxSensitiveAcctService
      .deleteTaxSensitiveDetails(
        this.GENERAL_CONFIG.base_url,
        details,
        settings,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          if (result?.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
            return;
          }
          this.gtwSnackbarService.openSnackBar('Data has been deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Error occurred while deleting.', 'gtw-snackbar--red');
        },
      });
  }

  save(): void {
    if (this.crudLoading) {
      return;
    }
    const details = this.loadSystemTaxsensitivitydata
      .filter((row: any) => row.saveFlag)
      .map((row: any) => this.buildSaveDetail(row));

    if (details.length === 0) {
      this.gtwSnackbarService.openSnackBar('No changes available to save.', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    const settings = details[0];
    this.taxSensitiveAcctService
      .saveTaxSensitiveDetails(
        this.GENERAL_CONFIG.base_url,
        details,
        settings,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.crudLoading = false;
          if (result?.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Data has been successfully save', 'gtw-snackbar--green');
            this.loadSystemTaxsensitivitydata.forEach((row: any) => {
              row.isEditable = false;
              row.saveFlag = false;
            });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  resetLookup(): void {
    this.isInitialized = false;
    this.fetchTaxSensitiveData();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private buildSaveDetail(row: any): any {
    const source = this.rowData && Object.keys(this.rowData).length > 0 ? this.rowData : this.gridData?.[0]?.data || {};
    return {
      type: 'E',
      object_id: source.object_id,
      tax_year: source.TAX_YEAR,
      scenario: this.globalService?.scenario ?? this.filterParams?.scenario,
      triggerchart: row.TRIGGER_CHART,
      triggeracct: row.TRIGGER_ACCT,
      postingacct: row.POSTING_ACCT,
      postingtac: row.POSTING_TAC,
      newtemplatevalue: row.dropdowndata ?? row.oldtemplatevalue,
      oldtemplatevalue: row.oldtemplatevalue,
      olduserdescvalue: row.olduserdescvalue,
      newuserdescvalue: row.USER_DESC,
      scdhmid: row.SCHDM_ID,
    };
  }

  private buildDeleteSettings(row: any): any {
    const source = this.rowData && Object.keys(this.rowData).length > 0 ? this.rowData : this.gridData?.[0]?.data || {};
    return {
      type: 'D',
      object_id: source.object_id,
      tax_year: source.TAX_YEAR,
      scenario: this.globalService?.scenario ?? this.filterParams?.scenario,
      trigger_acct: row.TRIGGER_ACCT,
      posting_tac: row.POSTING_TAC,
      postint_acct: row.POSTING_ACCT,
      scdhmid: row.SCHDM_ID,
    };
  }
}
